import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Phone, Mail, AlertTriangle, MapPin, Pencil } from "lucide-react";

import EmployeeEditor from "../components/EmployeeEditor";
import QuickCheckIn from "../components/QuickCheckIn";
import StatusPill from "../components/StatusPill";
import { api } from "../lib/api";
import { timeAgo } from "../lib/format";
import type { CurrentUser } from "../lib/types";

interface EmployeeDetail {
  oracle_id: string;
  first?: string;
  middle?: string;
  last?: string;
  full_name: string;
  nationality?: string | null;
  primary_phone?: string | null;
  primary_phone_masked?: string | null;
  email?: string | null;
  passport_no_masked?: string | null;
  residency_no_masked?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_relation?: string | null;
  emergency_contact_phone_masked?: string | null;
  home: {
    country: string | null;
    city: string | null;
    address: string | null;
    lat?: number | null;
    lng?: number | null;
    lat_lng_manual?: boolean;
  };
  current: {
    status: "home" | "relocated" | "in_transit" | "unknown";
    country: string | null;
    city: string | null;
    address: string | null;
    last_seen_at: string | null;
  };
  org: {
    unit_id?: string | null;
    unit_name: string | null;
    function: string | null;
    title: string | null;
    manager_oracle_id?: string | null;
    manager_name?: string | null;
  };
  family_count: number;
  red_flags: string[];
  family: { id: string; first: string; last: string; relationship?: string | null }[];
  travel: { id: string; kind: string; origin_city?: string | null; destination_city?: string | null; depart_at?: string | null; arrive_at?: string | null; note?: string | null }[];
  updates: {
    id: string;
    city?: string | null;
    country?: string | null;
    address?: string | null;
    note?: string | null;
    created_at?: string | null;
    source: string;
    employee_status?: string | null;
  }[];
}

function sourceLabel(source: string): string {
  switch (source) {
    case "whatsapp":
      return "via WhatsApp";
    case "web":
      return "via web";
    case "hr_manual":
      return "by HR";
    case "import":
      return "from import";
    default:
      return `via ${source}`;
  }
}

function checkinLocationLabel(
  u: {
    city?: string | null;
    country?: string | null;
    address?: string | null;
    employee_status?: string | null;
  },
  emp: EmployeeDetail,
): string {
  const explicit = [u.city, u.country].filter(Boolean).join(", ");
  if (explicit) return explicit;
  // For "at home" check-ins, fall back to the saved home address — the
  // public WhatsApp form doesn't ask for city/country when the employee
  // taps "I'm at home".
  if (u.employee_status === "home") {
    const home = [emp.home.city, emp.home.country].filter(Boolean).join(", ");
    return home ? `${home} (home)` : "Home";
  }
  return "—";
}

export default function EmployeeDetailPage() {
  const { oracleId = "" } = useParams();
  const [editing, setEditing] = useState(false);
  const me = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get<CurrentUser>("/api/me"),
  });
  const q = useQuery({
    queryKey: ["employee", oracleId],
    queryFn: () => api.get<EmployeeDetail>(`/api/employees/${encodeURIComponent(oracleId)}`),
  });

  if (q.isPending) return <div className="p-6 text-slate-400">Loading…</div>;
  if (q.error || !q.data) return <div className="p-6 text-red-300">Failed to load employee.</div>;
  const emp = q.data;
  const canEdit = me.data?.role === "super_admin";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px] gap-4 sm:gap-5">
      <div className="space-y-3 sm:space-y-4 min-w-0">
        <div className="flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200">
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          {canEdit && (
            <button
              onClick={() => setEditing(true)}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 ring-1 ring-slate-700 text-slate-200"
            >
              <Pencil size={13} /> Edit
            </button>
          )}
        </div>

        <div className="rounded-xl bg-[var(--color-surface)]/70 ring-1 ring-[var(--color-border)] p-5">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-2xl font-semibold text-slate-100">{emp.full_name}</div>
              <div className="text-slate-400 mt-1 text-sm">
                {emp.org.title || "—"} · {emp.org.function || emp.org.unit_name || "—"}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <StatusPill status={emp.current.status} />
                <span className="text-xs text-slate-500">
                  Last seen {timeAgo(emp.current.last_seen_at)}{" "}
                  {emp.current.city ? `· ${emp.current.city}` : ""}{" "}
                  {emp.current.country ? `· ${emp.current.country}` : ""}
                </span>
                {emp.red_flags.length > 0 && (
                  <span
                    className="relative inline-flex group"
                    tabIndex={0}
                  >
                    <span className="inline-flex items-center gap-1 text-xs text-amber-300 ring-1 ring-amber-400/30 bg-amber-500/10 px-2 py-0.5 rounded-full cursor-help">
                      <AlertTriangle size={12} /> {emp.red_flags.length} red flag
                      {emp.red_flags.length === 1 ? "" : "s"}
                    </span>
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute left-0 top-full mt-1 z-20 hidden group-hover:block group-focus-within:block min-w-[220px] max-w-[320px] rounded-md bg-slate-900 ring-1 ring-amber-400/40 shadow-lg p-2 text-xs text-amber-100"
                    >
                      <div className="text-[10px] uppercase tracking-wide text-amber-300/80 mb-1">
                        Red flags
                      </div>
                      <ul className="space-y-1 list-disc list-inside">
                        {emp.red_flags.map((f, i) => (
                          <li key={i}>{f}</li>
                        ))}
                      </ul>
                    </span>
                  </span>
                )}
              </div>
            </div>
            <div className="text-right text-sm">
              <KV icon={<Phone size={13} />} value={emp.primary_phone || emp.primary_phone_masked} />
              <KV icon={<Mail size={13} />} value={emp.email} />
            </div>
          </div>
        </div>

        {canEdit && (
          <QuickCheckIn
            oracleId={emp.oracle_id}
            currentStatus={emp.current.status}
            homeCountry={emp.home.country}
            homeCity={emp.home.city}
            homeAddress={emp.home.address}
            homeLat={emp.home.lat ?? null}
            homeLng={emp.home.lng ?? null}
          />
        )}

        <Section title="Recent check-ins" icon={<MapPin size={16} />}>
          {emp.updates.length === 0 ? (
            <div className="text-sm text-slate-500 px-1 py-3">No check-ins yet.</div>
          ) : (
            <ul className="space-y-2">
              {emp.updates.map((u) => (
                <li
                  key={u.id}
                  className="text-sm flex gap-3 items-start py-1.5"
                >
                  <span className="text-xs text-slate-500 w-20 shrink-0 mt-0.5">
                    {timeAgo(u.created_at ?? null)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      {u.employee_status && (
                        <StatusPill
                          status={
                            u.employee_status as
                              | "home"
                              | "relocated"
                              | "in_transit"
                              | "unknown"
                          }
                        />
                      )}
                      <span className="text-slate-200">
                        {checkinLocationLabel(u, emp)}
                      </span>
                      <span className="text-[11px] text-slate-500 ml-auto">
                        {sourceLabel(u.source)}
                      </span>
                    </div>
                    {u.note && (
                      <div className="text-xs text-slate-400 mt-0.5">
                        {u.note}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      <aside className="space-y-3 sm:space-y-4 min-w-0">
        <Section title="Identity">
          <Row label="Oracle ID" value={emp.oracle_id} />
          <Row label="Nationality" value={emp.nationality} />
          <Row label="Passport" value={emp.passport_no_masked || "—"} />
          <Row label="Residency" value={emp.residency_no_masked || "—"} />
        </Section>
        <Section title="Home">
          <Row label="City" value={emp.home.city} />
          <Row label="Country" value={emp.home.country} />
          <Row label="Address" value={emp.home.address} />
        </Section>
        <Section title="Emergency contact">
          <Row label="Name" value={emp.emergency_contact_name} />
          <Row label="Relation" value={emp.emergency_contact_relation} />
          <Row label="Phone" value={emp.emergency_contact_phone_masked || "—"} />
        </Section>
        <Section title={`Family (${emp.family.length})`}>
          {emp.family.length === 0 ? (
            <div className="text-xs text-slate-500">Not yet captured.</div>
          ) : (
            <ul className="space-y-1">
              {emp.family.map((f) => (
                <li key={f.id} className="text-sm text-slate-300">
                  {f.first} {f.last}
                  {f.relationship && (
                    <span className="text-xs text-slate-500"> · {f.relationship}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Section>
      </aside>

      {editing && (
        <EmployeeEditor
          employee={{
            oracle_id: emp.oracle_id,
            first: emp.first,
            middle: emp.middle,
            last: emp.last,
            full_name: emp.full_name,
            nationality: emp.nationality ?? null,
            primary_phone: emp.primary_phone ?? null,
            email: emp.email ?? null,
            home: {
              country: emp.home.country,
              city: emp.home.city,
              address: emp.home.address,
              lat: emp.home.lat ?? null,
              lng: emp.home.lng ?? null,
              lat_lng_manual: emp.home.lat_lng_manual ?? false,
            },
            org: {
              unit_id: emp.org.unit_id ?? null,
              unit_name: emp.org.unit_name,
              function: emp.org.function,
              title: emp.org.title,
              manager_oracle_id: emp.org.manager_oracle_id ?? null,
              manager_name: emp.org.manager_name ?? null,
            },
            emergency_contact_name: emp.emergency_contact_name ?? null,
            emergency_contact_relation: emp.emergency_contact_relation ?? null,
            red_flags: emp.red_flags,
            current: { status: emp.current.status },
          }}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl bg-[var(--color-surface)]/70 ring-1 ring-[var(--color-border)] p-4">
      <div className="text-xs uppercase tracking-wide text-slate-400 flex items-center gap-1 mb-2">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between text-sm gap-3 py-1">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-200 text-right">{value || "—"}</span>
    </div>
  );
}

function KV({ icon, value }: { icon: React.ReactNode; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-1.5 justify-end text-slate-300">
      {icon}
      {value}
    </div>
  );
}
